import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRestoreBackup } from '@/hooks/useRestoreBackup';
import { useManuscripts } from '@/hooks/useManuscripts';
import { useEncryption } from '@/hooks/useEncryption';
import { useCloudSync } from '@/hooks/useCloudSync';
import { SetupScreen, UnlockScreen } from './E2EEncryptionFlow';
import { supabase } from '@/integrations/supabase/client';
import { IconLoader2 } from '@tabler/icons-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { data: manuscripts, isLoading: manuscriptsLoading } = useManuscripts();
  const { restoreAllFromSupabase } = useRestoreBackup();
  const { 
    isReady: encryptionReady, 
    isSetup: encryptionSetup, 
    isLoading: encryptionLoading 
  } = useEncryption();
  
  const { isSyncing } = useCloudSync();

  // Legacy Migration State
  const [isRestoringLegacy, setIsRestoringLegacy] = useState(false);
  const [checkedLegacy, setCheckedLegacy] = useState(false);

  // Automatic Legacy Migration Handler
  // If E2E is not setup, but they have old unencrypted/user.id backups, pull them first.
  useEffect(() => {
    async function checkAndRestoreLegacy() {
      if (!user?.id || encryptionSetup !== false || checkedLegacy || isRestoringLegacy) return;
      
      setIsRestoringLegacy(true);
      try {
        console.log("[E2E Migration] Checking for legacy user backups...");
        const { count, error } = await supabase
          .from('manuscript_backups')
          .select('id', { count: 'exact', head: true })
          .neq('manuscript_id', '__verification__')
          .eq('user_id', user.id);

        if (error) throw error;

        if (count && count > 0) {
          console.log(`[E2E Migration] Found ${count} legacy backups. Restoring...`);
          await restoreAllFromSupabase();
          console.log("[E2E Migration] Legacy restoration complete.");
        }
      } catch (err) {
        console.error("[E2E Migration] Failed to restore legacy backups:", err);
      } finally {
        setIsRestoringLegacy(false);
        setCheckedLegacy(true);
      }
    }
    checkAndRestoreLegacy();
  }, [user?.id, encryptionSetup, checkedLegacy, isRestoringLegacy, restoreAllFromSupabase]);

  const showInitialSyncLoader = isSyncing && manuscripts && manuscripts.length === 0;
  const isCheckingLegacy = !!user?.id && encryptionSetup === false && !checkedLegacy;

  // Handle loading states
  if (authLoading || encryptionLoading || manuscriptsLoading || showInitialSyncLoader || isRestoringLegacy || isCheckingLegacy) {
    let message = "Loading...";
    if (isRestoringLegacy || isCheckingLegacy) message = "Restoring legacy manuscripts...";
    else if (showInitialSyncLoader) message = "Syncing from cloud...";
    else if (encryptionLoading) message = "Initializing encryption...";

    return (
      <div className="h-screen overflow-hidden bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          {(showInitialSyncLoader || isRestoringLegacy || encryptionLoading) && (
            <IconLoader2 className="animate-spin text-primary" size={32} />
          )}
          <p className="text-foreground text-lg">{message}</p>
        </div>
      </div>
    );
  }

  // Redirect to landing page if not authenticated
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Intercept and show setup screen if E2E setup is missing
  if (encryptionSetup === false) {
    return <SetupScreen onSuccess={() => window.location.reload()} />;
  }

  // Intercept and show unlock screen if E2E setup exists but not unlocked in memory
  if (encryptionSetup === true && !encryptionReady) {
    return <UnlockScreen onSuccess={() => window.location.reload()} />;
  }

  // Fail-closed: do not mount application if encryption state is unknown or errored
  if (encryptionSetup === null) {
    return (
      <div className="h-screen overflow-hidden bg-background flex items-center justify-center p-6 text-center">
        <div className="flex flex-col items-center max-w-md gap-4">
          <p className="text-foreground text-lg font-medium">Unable to verify encryption security</p>
          <p className="text-muted-foreground text-sm">
            Could not verify end-to-end encryption status. Please check your internet connection or reload.
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // Allow mounting application once encryption is ready
  return <>{children}</>;
}
